import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import GroupsIcon from "@mui/icons-material/Groups";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate, useSearchParams } from "react-router-dom";

import BrandPagination from "../../layouts/Pagination";
import SearchBar from "../../shared/components/SearchBar";
import BrandSpinner from "../../shared/components/BrandSpinner";
import BackButton from "../../shared/components/BackButton";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import useRowSelection from "../../hooks/useRowSelection";
import api from "../../services/api/axios";
import {
  ACTIONS_HEADER_BTN_SX,
  ACTION_MENU_ICON_SX,
  ACTION_MENU_ICON_TONES,
  ACTION_MENU_PAPER_SX,
  PRIMARY_BUTTON_SX,
  SECONDARY_BUTTON_SX,
} from "../../theme/buttonstyles";
import { FILTER_STYLES } from "../../theme/filterstyles";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import MembershipMemberDialog from "./MembershipMemberDialog";
import MembershipMembersTable from "./MembershipMembersTable";
import useMembershipMembersData from "./hooks/useMembershipMembersData";

const PAGE_BG = "#f7f9fc";

const CARD_SX = {
  border: `1.5px solid ${BRAND_BLUE}`,
  borderRadius: 2,
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
  background: "#ffffff",
  overflow: "hidden",
};

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "prospect", label: "Prospect" },
  { value: "lapsed", label: "Lapsed" },
  { value: "suspended", label: "Suspended" },
  { value: "cancelled", label: "Cancelled" },
];

const MEMBER_TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "Business", label: "Business" },
  { value: "Nonprofit", label: "Nonprofit" },
  { value: "Government", label: "Government" },
  { value: "Individual", label: "Individual" },
  { value: "Investor", label: "Investor" },
  { value: "Partner", label: "Partner" },
];

const TIER_OPTIONS = [
  { value: "", label: "All Tiers" },
  { value: "Basic", label: "Basic" },
  { value: "Bronze", label: "Bronze" },
  { value: "Silver", label: "Silver" },
  { value: "Gold", label: "Gold" },
  { value: "Platinum", label: "Platinum" },
  { value: "Premier", label: "Premier" },
  { value: "Unspecified", label: "Unspecified" },
];

const RENEWAL_OPTIONS = [
  { value: "", label: "All Renewals" },
  { value: "overdue", label: "Overdue" },
  { value: "next_30", label: "Next 30 Days" },
  { value: "next_90", label: "Next 90 Days" },
  { value: "no_date", label: "No Renewal Date" },
];

const ENGAGEMENT_OPTIONS = [
  { value: "", label: "All Engagement" },
  { value: "high", label: "High" },
  { value: "moderate", label: "Moderate" },
  { value: "low", label: "Low" },
  { value: "unscored", label: "Unscored" },
];

function todayInputValue() {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${today.getFullYear()}-${month}-${day}`;
}

function enrichMembers(members, memberships) {
  const byMemberId = new Map();
  memberships.forEach((membership) => {
    const existing = byMemberId.get(membership.member_id);
    if (!existing) {
      byMemberId.set(membership.member_id, membership);
      return;
    }
    const existingDate = existing.renewal_date || "";
    const nextDate = membership.renewal_date || "";
    if (nextDate && (!existingDate || nextDate < existingDate)) {
      byMemberId.set(membership.member_id, membership);
    }
  });

  return members.map((member) => {
    const membership = byMemberId.get(member.id);
    return {
      ...member,
      membership_id: membership?.id || null,
      membership_tier: membership?.membership_level || member.membership_tier || "",
      renewal_date: membership?.renewal_date || member.renewal_date || "",
      dues_amount: membership?.dues_amount || "",
      paid: Boolean(membership?.paid),
      paid_amount: membership?.paid_amount || "",
      paid_at: membership?.paid_at || "",
      engagement_score: member.engagement_score ?? "",
      engagement_category: member.engagement_category || "unscored",
    };
  });
}

function isInRenewalWindow(value, filter) {
  if (!filter) return true;
  if (!value) return filter === "no_date";

  const renewalDate = new Date(`${value}T00:00:00`);
  if (Number.isNaN(renewalDate.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((renewalDate - today) / 86400000);

  if (filter === "overdue") return diffDays < 0;
  if (filter === "next_30") return diffDays >= 0 && diffDays <= 30;
  if (filter === "next_90") return diffDays >= 0 && diffDays <= 90;
  return true;
}

function applyDisplayFilters(members, filters) {
  return members.filter((member) => {
    if (filters.membership_tier && (member.membership_tier || "Unspecified") !== filters.membership_tier) {
      return false;
    }
    if (
      filters.engagement_category &&
      (member.engagement_category || "unscored") !== filters.engagement_category
    ) {
      return false;
    }
    return isInRenewalWindow(member.renewal_date, filters.renewal_date);
  });
}

function MembershipHeader({ searchTerm, onSearchChange, selectedCount, onActionsClick }) {
  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 5,
        borderBottom: "1px solid #dce4ef",
        background: "#ffffff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: { xs: 2, md: 3 },
          py: 1.75,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
          <BackButton to="/membership" />
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              background: BRAND_GREEN,
              color: "#ffffff",
              display: "grid",
              placeItems: "center",
            }}
          >
            <GroupsIcon sx={{ fontSize: 19 }} />
          </Box>
          <Typography sx={{ fontSize: 21, fontWeight: 800, color: BRAND_BLUE, lineHeight: 1.05 }}>
            Members
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 240 }}>
          <SearchBar
            value={searchTerm}
            onChange={onSearchChange}
            placeholder="Search organization name, email, or location..."
            maxWidth={1700}
          />
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          {selectedCount > 0 ? (
            <Chip
              label={`${selectedCount} selected`}
              size="small"
              sx={{
                height: 26,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: "rgba(0,51,108,0.08)",
                color: BRAND_BLUE,
              }}
            />
          ) : null}
          <Button endIcon={<KeyboardArrowDownIcon />} onClick={onActionsClick} sx={ACTIONS_HEADER_BTN_SX}>
            Actions
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

function FilterBar({ filters, onChange }) {
  return (
    <Card sx={{ ...CARD_SX, mb: 2 }}>
      <CardContent sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
        <FormControl size="small" sx={FILTER_STYLES.formControl}>
          <Select
            value={filters.status}
            onChange={(event) => onChange("status", event.target.value)}
            {...FILTER_STYLES.select}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={FILTER_STYLES.formControl}>
          <Select
            value={filters.member_type}
            onChange={(event) => onChange("member_type", event.target.value)}
            {...FILTER_STYLES.select}
          >
            {MEMBER_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={FILTER_STYLES.formControl}>
          <Select
            value={filters.membership_tier}
            onChange={(event) => onChange("membership_tier", event.target.value)}
            {...FILTER_STYLES.select}
          >
            {TIER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={FILTER_STYLES.formControl}>
          <Select
            value={filters.renewal_date}
            onChange={(event) => onChange("renewal_date", event.target.value)}
            {...FILTER_STYLES.select}
          >
            {RENEWAL_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={FILTER_STYLES.formControl}>
          <Select
            value={filters.engagement_category}
            onChange={(event) => onChange("engagement_category", event.target.value)}
            {...FILTER_STYLES.select}
          >
            {ENGAGEMENT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </CardContent>
    </Card>
  );
}

function BulkTagDialog({ open, count, saving, onClose, onSubmit }) {
  const [tagName, setTagName] = useState("");
  const [color, setColor] = useState("#648315");

  useEffect(() => {
    if (open) {
      setTagName("");
      setColor("#648315");
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ color: BRAND_BLUE, fontWeight: 800 }}>Bulk Tag Members</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography sx={{ color: "#52677f", fontSize: 14 }}>
            Apply a tag to {count} selected member{count === 1 ? "" : "s"}.
          </Typography>
          <TextField
            label="Tag Name"
            value={tagName}
            onChange={(event) => setTagName(event.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Color"
            type="color"
            value={color}
            onChange={(event) => setColor(event.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={SECONDARY_BUTTON_SX}>Cancel</Button>
        <Button
          onClick={() => onSubmit({ tagName: tagName.trim(), color })}
          disabled={saving || !tagName.trim()}
          sx={PRIMARY_BUTTON_SX}
        >
          {saving ? "Tagging..." : "Apply Tag"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PaymentDialog({ open, member, saving, onClose, onSubmit }) {
  const [values, setValues] = useState({ paid_amount: "", paid_at: todayInputValue() });

  useEffect(() => {
    if (open) {
      setValues({
        paid_amount: member?.paid_amount || member?.dues_amount || "",
        paid_at: member?.paid_at || todayInputValue(),
      });
    }
  }, [member, open]);

  const amountIsValid = values.paid_amount !== "" && Number(values.paid_amount) >= 0;
  const canSubmit = Boolean(values.paid_at) && amountIsValid;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ color: BRAND_BLUE, fontWeight: 800 }}>Record Payment</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography sx={{ color: "#52677f", fontSize: 14, fontWeight: 700 }}>
            {member?.name || "Member"}
          </Typography>
          <TextField
            label="Amount Paid"
            type="number"
            value={values.paid_amount}
            onChange={(event) => setValues((prev) => ({ ...prev, paid_amount: event.target.value }))}
            fullWidth
            required
            inputProps={{ min: 0, step: "0.01" }}
          />
          <TextField
            label="Date Paid"
            type="date"
            value={values.paid_at}
            onChange={(event) => setValues((prev) => ({ ...prev, paid_at: event.target.value }))}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={SECONDARY_BUTTON_SX}>Cancel</Button>
        <Button
          onClick={() => onSubmit(values)}
          disabled={saving || !canSubmit}
          sx={PRIMARY_BUTTON_SX}
        >
          {saving ? "Saving..." : "Save Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function MembershipMembers() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNewRoute = window.location.pathname.endsWith("/new");
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const [filters, setFilters] = useState({
    status: "",
    member_type: "",
    membership_tier: searchParams.get("membership_tier") || "",
    renewal_date: "",
    engagement_category: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [orderBy, setOrderBy] = useState("created_at");
  const [orderDirection, setOrderDirection] = useState("desc");
  const [memberships, setMemberships] = useState([]);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeMember, setActiveMember] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const [bulkTagSaving, setBulkTagSaving] = useState(false);
  const [paymentMember, setPaymentMember] = useState(null);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const {
    members,
    totalCount,
    loading,
    error,
    refetch,
  } = useMembershipMembersData({
    page,
    pageSize,
    searchTerm: debouncedSearchTerm,
    filters,
    orderBy,
    orderDirection,
  });

  const visibleMembers = useMemo(
    () => enrichMembers(members, memberships),
    [members, memberships],
  );
  const filteredVisibleMembers = useMemo(
    () => applyDisplayFilters(visibleMembers, filters),
    [filters, visibleMembers],
  );

  const {
    selectedIds,
    selectedCount,
    allSelected,
    someSelected,
    toggleOne,
    togglePage,
    clear: clearSelection,
  } = useRowSelection(filteredVisibleMembers, (member) => member.id);

  const loadMemberships = useCallback(async () => {
    try {
      const response = await api.get("/membership/memberships", {
        params: { limit: 500, offset: 0, sort_by: "renewal_date", sort_dir: "asc" },
      });
      setMemberships(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch {
      setMemberships([]);
    }
  }, []);

  useEffect(() => {
    loadMemberships();
  }, [loadMemberships]);

  useEffect(() => {
    if (isNewRoute) {
      setActiveMember(null);
      setDialogLoading(false);
      setDialogOpen(true);
    } else {
      setDialogOpen(false);
      setActiveMember(null);
    }
  }, [isNewRoute]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleSort = useCallback(
    (key) => {
      const nextDirection = orderBy === key && orderDirection === "asc" ? "desc" : "asc";
      setOrderBy(key);
      setOrderDirection(nextDirection);
      setPage(1);
    },
    [orderBy, orderDirection],
  );

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setActiveMember(null);
    navigate("/membership/members");
  }, [navigate]);

  const openEditDialog = useCallback((member) => {
    setActiveMember(member);
    setDialogLoading(false);
    setDialogOpen(true);
  }, []);

  const handleSaveMember = useCallback(
    async (payload) => {
      setSaving(true);
      try {
        if (activeMember?.id) {
          await api.patch(`/membership/members/${activeMember.id}`, payload);
          showSnackbar("Member updated.");
        } else {
          await api.post("/membership/members", payload);
          showSnackbar("Member added.");
        }
        closeDialog();
        await refetch();
      } catch (err) {
        const detail = err.response?.data?.detail;
        showSnackbar(typeof detail === "string" ? detail : "Failed to save member.", "error");
      } finally {
        setSaving(false);
      }
    },
    [activeMember?.id, closeDialog, refetch, showSnackbar],
  );

  const handleBulkTag = useCallback(
    async ({ tagName, color }) => {
      const ids = Array.from(selectedIds);
      if (!ids.length || !tagName) return;
      setBulkTagSaving(true);
      try {
        const results = await Promise.allSettled(
          ids.map((memberId) =>
            api.post("/membership/tags", {
              name: tagName,
              color,
              entity_type: "member",
              entity_id: Number(memberId),
            }),
          ),
        );
        const succeeded = results.filter((result) => result.status === "fulfilled").length;
        const failed = ids.length - succeeded;
        setBulkTagOpen(false);
        if (succeeded) showSnackbar(`Tagged ${succeeded} member${succeeded === 1 ? "" : "s"}.`);
        if (failed) showSnackbar(`${failed} tag assignment${failed === 1 ? "" : "s"} failed.`, "warning");
      } catch {
        showSnackbar("Failed to tag members.", "error");
      } finally {
        setBulkTagSaving(false);
      }
    },
    [selectedIds, showSnackbar],
  );

  const handlePaymentToggle = useCallback(
    async (member, paid) => {
      if (paid) {
        setPaymentMember(member);
        return;
      }
      if (!member.membership_id) return;

      setPaymentSaving(true);
      try {
        await api.patch(`/membership/memberships/${member.membership_id}`, {
          paid: false,
          paid_amount: null,
          paid_at: null,
        });
        await loadMemberships();
        showSnackbar("Payment marked unpaid.");
      } catch (err) {
        const detail = err.response?.data?.detail;
        showSnackbar(typeof detail === "string" ? detail : "Failed to update payment.", "error");
      } finally {
        setPaymentSaving(false);
      }
    },
    [loadMemberships, showSnackbar],
  );

  const handleSavePayment = useCallback(
    async (values) => {
      if (!paymentMember?.id) return;
      setPaymentSaving(true);
      try {
        const payload = {
          paid: true,
          paid_amount: values.paid_amount,
          paid_at: values.paid_at,
        };
        if (paymentMember.membership_id) {
          await api.patch(`/membership/memberships/${paymentMember.membership_id}`, payload);
        } else {
          await api.post("/membership/memberships", {
            member_id: paymentMember.id,
            ...payload,
          });
        }
        setPaymentMember(null);
        await loadMemberships();
        showSnackbar("Payment recorded.");
      } catch (err) {
        const detail = err.response?.data?.detail;
        showSnackbar(typeof detail === "string" ? detail : "Failed to record payment.", "error");
      } finally {
        setPaymentSaving(false);
      }
    },
    [loadMemberships, paymentMember, showSnackbar],
  );

  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const showEmpty = !loading && !error && filteredVisibleMembers.length === 0;

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: PAGE_BG, pb: 4 }}>
      <MembershipHeader
        searchTerm={searchTerm}
        onSearchChange={(value) => {
          setSearchTerm(value);
          setPage(1);
        }}
        selectedCount={selectedCount}
        onActionsClick={(event) => setActionsAnchorEl(event.currentTarget)}
      />

      <Box sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>
        <FilterBar filters={filters} onChange={handleFilterChange} />

        <Card sx={CARD_SX}>
          {loading ? (
            <BrandSpinner compact label="Loading members..." py={8} />
          ) : error ? (
            <CardContent sx={{ p: 4 }}>
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => refetch()}>
                    Retry
                  </Button>
                }
              >
                {error}
              </Alert>
            </CardContent>
          ) : showEmpty ? (
            <CardContent sx={{ p: 5, textAlign: "center" }}>
              <Typography sx={{ color: BRAND_BLUE, fontWeight: 800, fontSize: 20 }}>
                No members found
              </Typography>
              <Typography sx={{ color: "#52677f", mt: 1 }}>
                Add a member or adjust search and filters to find existing records.
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() => navigate("/membership/members/new")}
                sx={{ ...PRIMARY_BUTTON_SX, mt: 3 }}
              >
                Add Member
              </Button>
            </CardContent>
          ) : (
            <MembershipMembersTable
              members={filteredVisibleMembers}
              orderBy={orderBy}
              orderDirection={orderDirection}
              onSort={handleSort}
              selectedIds={selectedIds}
              allSelected={allSelected}
              someSelected={someSelected}
              toggleOne={toggleOne}
              togglePage={togglePage}
              onOpenProfile={(member) => navigate(`/membership/members/${member.id}`)}
              onEdit={openEditDialog}
              onPaymentToggle={handlePaymentToggle}
            />
          )}
        </Card>

        {!showEmpty && !error ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <BrandPagination
              page={page}
              count={totalPages}
              onChange={(_event, value) => setPage(value)}
            />
          </Box>
        ) : null}
      </Box>

      <Menu
        anchorEl={actionsAnchorEl}
        open={Boolean(actionsAnchorEl)}
        onClose={() => setActionsAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: ACTION_MENU_PAPER_SX }}
      >
        <MenuItem
          onClick={() => {
            setActionsAnchorEl(null);
            navigate("/membership/members/new");
          }}
        >
          <ListItemIcon sx={{ ...ACTION_MENU_ICON_SX, ...ACTION_MENU_ICON_TONES.add }}>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Add Member</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setActionsAnchorEl(null);
            refetch();
            loadMemberships();
          }}
        >
          <ListItemIcon sx={{ ...ACTION_MENU_ICON_SX, ...ACTION_MENU_ICON_TONES.refresh }}>
            <RefreshIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Refresh</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={selectedCount === 0}
          onClick={() => {
            setActionsAnchorEl(null);
            setBulkTagOpen(true);
          }}
        >
          <ListItemIcon sx={{ ...ACTION_MENU_ICON_SX, ...ACTION_MENU_ICON_TONES.save }}>
            <LocalOfferIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Bulk Tag Selected</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={selectedCount === 0}
          onClick={() => {
            setActionsAnchorEl(null);
            clearSelection();
          }}
        >
          <ListItemIcon sx={{ ...ACTION_MENU_ICON_SX, ...ACTION_MENU_ICON_TONES.clear }}>
            <ClearAllIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Clear Selection</ListItemText>
        </MenuItem>
      </Menu>

      <MembershipMemberDialog
        open={dialogOpen}
        member={activeMember}
        loading={dialogLoading}
        saving={saving}
        onClose={closeDialog}
        onSubmit={handleSaveMember}
      />

      <BulkTagDialog
        open={bulkTagOpen}
        count={selectedCount}
        saving={bulkTagSaving}
        onClose={() => setBulkTagOpen(false)}
        onSubmit={handleBulkTag}
      />

      <PaymentDialog
        open={Boolean(paymentMember)}
        member={paymentMember}
        saving={paymentSaving}
        onClose={() => setPaymentMember(null)}
        onSubmit={handleSavePayment}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
